import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import "./typesoffunction.css";

const TypesOfFunction = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("venue");
  const [functions, setFunctions] = useState([]);
  const [venues, setVenues] = useState([]);
  const [services, setServices] = useState([]);
  const [prices, setPrices] = useState([]);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const API_URL = process.env.REACT_APP_API_URL;
  // Selected filters
  const [selectedFunctions, setSelectedFunctions] = useState([]);
  const [selectedVenues, setSelectedVenues] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedPrices, setSelectedPrices] = useState([]);
  const [categories, setCategories] = useState([]); // categories from API

  useEffect(() => {
    fetchCategories();
    fetchDataForTab("venue");
  }, []);

  useEffect(() => {
    const query = searchParams.get("query");
    if (query) {
      // Find matching categories to set the active tab
      const matchingService = services.find(s => s.toLowerCase().includes(query.toLowerCase()));
      if (matchingService) {
        setActiveTab(matchingService);
        fetchDataForTab(matchingService);
      } else {
        // If no exact match, search across all data
        searchAcrossAll(query);
      }
      // Optionally, apply as a filter (e.g., to services)
      setSelectedServices([query]);
    }
  }, [searchParams, services]);

  useEffect(() => {
      const fetchCategories = async () => {
        try {
          const res = await fetch(`${API_URL}/catagory`);
          const data = await res.json();
          const categoryNames = data.map((cat) =>
            typeof cat === "string" ? cat : cat.name || ""
          );
          setCategories(categoryNames); // array of category names
        } catch (err) {
          console.error("Failed to fetch categories:", err);
        }
      };
      fetchCategories();
    }, [API_URL]);
  
  const searchAcrossAll = async (query) => {
    try {
      const res = await axios.get(`${API_URL}/catagory`);
      const allData = res.data || [];
      const lowerQuery = query.toLowerCase();
      const matchingItems = allData.filter(item => 
        (item.name && item.name.toLowerCase().includes(lowerQuery)) ||
        (item.subcategory && (
          (Array.isArray(item.subcategory) ? item.subcategory.some(sub => sub.toLowerCase().includes(lowerQuery)) : item.subcategory.toLowerCase().includes(lowerQuery))
        )) ||
        (item.services && item.services.some(s => s.toLowerCase().includes(lowerQuery))) ||
        (item.description && item.description.toLowerCase().includes(lowerQuery))
      );
      setItems(matchingItems);
      setFilteredItems(matchingItems);
      // Set activeTab to a general search view if needed, e.g., "search"
      // For now, keep as is or set to "venue" for fallback
    } catch (err) {
      console.error("Error searching data:", err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories`);
      const data = res.data || [];

      const allServices = data
        .filter((cat) => cat.category === "Vendors")
        .flatMap((cat) => cat.subcategory || []);
      setServices([...new Set(allServices)]);

      const allFunctions = data
        .filter((cat) => cat.category === "Functions")
        .flatMap((cat) => cat.subcategory || []);
      setFunctions([...new Set(allFunctions)]);

      const allVenues = data
        .filter((cat) => cat.category === "Venues")
        .flatMap((cat) => cat.subcategory || []);
      setVenues([...new Set(allVenues)]);

      const allPrices = data
        .filter((cat) => cat.category === "Prices")
        .flatMap((cat) => cat.subcategory || []);
      setPrices([...new Set(allPrices)]);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchDataForTab = async (tabName) => {
    try {
      const res = await axios.get(`${API_URL}/catagory`);
      const allData = res.data || [];

      let filtered;
      if (tabName === "venue") {
        filtered = allData.filter((item) => item.category === "Venues");
      } else {
        filtered = allData.filter((item) =>
          item.services?.includes(tabName)
        );
      }
      setItems(filtered);
      setFilteredItems(filtered);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    fetchDataForTab(tabName);
    resetFilters();
  };

  const resetFilters = () => {
    setSelectedFunctions([]);
    setSelectedVenues([]);
    setSelectedServices([]);
    setSelectedPrices([]);
  };

  // Apply filters whenever selected filters change
  useEffect(() => {
    let filtered = [...items];

    if (selectedFunctions.length > 0) {
      filtered = filtered.filter((item) =>
        selectedFunctions.includes(item.subcategory)
      );
    }

    if (selectedVenues.length > 0) {
      filtered = filtered.filter((item) =>
        selectedVenues.includes(item.subcategory)
      );
    }

    if (selectedServices.length > 0) {
      filtered = filtered.filter((item) =>
        item.services?.some((service) => selectedServices.includes(service))
      );
    }

    if (selectedPrices.length > 0) {
      filtered = filtered.filter((item) =>
        selectedPrices.includes(item.price?.toString())
      );
    }

    setFilteredItems(filtered);
  }, [selectedFunctions, selectedVenues, selectedServices, selectedPrices, items]);

  const handleCheckboxChange = (value, setFilterState) => {
    setFilterState((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };
  

  return (
    <div className="function-page">
      {/* Left Filter Section */}
      <div className="filter-panel">
        <h3>Filters</h3>

        <div className="filter-section">
          <h4>Functions</h4>
          <div className="checkbox-list">
            {functions.map((fn, idx) => (
              <label key={idx} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={selectedFunctions.includes(fn)}
                  onChange={() => handleCheckboxChange(fn, setSelectedFunctions)}
                />
                {fn}
              </label>
            ))}
          </div>
        </div>

        <div className="filter-section">
          <h4>Venues</h4>
          <div className="checkbox-list">
            {venues.map((vn, idx) => (
              <label key={idx} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={selectedVenues.includes(vn)}
                  onChange={() => handleCheckboxChange(vn, setSelectedVenues)}
                />
                {vn}
              </label>
            ))}
          </div>
        </div>

        <div className="filter-section">
          <h4>Vendors</h4>
          <div className="checkbox-list">
            {services.map((service, idx) => (
              <label key={idx} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={selectedServices.includes(service)}
                  onChange={() => handleCheckboxChange(service, setSelectedServices)}
                />
                {service}
              </label>
            ))}
          </div>
        </div>

        <div className="filter-section">
          <h4>Prices</h4>
          <div className="checkbox-list">
            {prices.map((price, idx) => (
              <label key={idx} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={selectedPrices.includes(price)}
                  onChange={() => handleCheckboxChange(price, setSelectedPrices)}
                />
                {price}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Right Content Section */}
      <div className="content-panel">
        <div className="tabs">
          <button
            className={activeTab === "venue" ? "active" : ""}
            onClick={() => handleTabClick("venue")}
          >
            Venue
          </button>
          {services.map((service, idx) => (
            <button
              key={idx}
              className={activeTab === service ? "active" : ""}
              onClick={() => handleTabClick(service)}
            >
              {service}
            </button>
          ))}
        </div>

        <div className="cards-container">
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <div className="card" key={item._id}>
                {item.image && (
                  <img src={`${API_URL}/${item.image}`} alt={item.name} className="card-image" />
                )}
                <h4>{item.name || "Unnamed"}</h4>
                <p>{item.description || "No description available"}</p>
                 {item.subcategory && (
          <p>Functions: {Array.isArray(item.subcategory) ? item.subcategory.join(" , ") : item.subcategory}</p>
        )}
                <p>
                  Subcategory:{" "}
                  {Array.isArray(item.subcategory) ? item.subcategory.join(", ") : item.subcategory}
                </p>
                <p>Location: {item.location || "Not provided"}</p>
                <p>
                  Price:{" "}
                  {item.price ? `₹${item.price.toLocaleString()}` : "Price not available"}
                </p>
                {item.services && item.services.length > 0 && (
                  <p>Services: {item.services.join(" , ")}</p>
                )}
              </div>
            ))
          ) : (
            <p>No results found for "{searchParams.get('query')}".</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TypesOfFunction;